import { useMemo } from "react";

import styled from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { Table } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Button, Card } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { allTrackers } from "app/services/track/track";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { entityTypeMap } from "pro-features/components/AutocompleteAdIntelligence/AutocompleteAdIntelligenceHelper";
import { displayAdvertisingNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/displayAdvertising/displayAdvertisingNavigator";
import { publisherAnalysisNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/publisherAnalysis/publisherAnalysisNavigator";

import { useDisplayAdvertiserPublisherDataAndParams } from "../../../common/hooks/useDisplayAdvertiserPublisherDataAndParams";
import { usePerformanceQuery } from "../../../common/overview/hooks/usePerformanceQuery";
import { isRequestedDateBeforeMay2025 } from "../../../common/overview/utils/utils";
import { ContentBlur } from "../../../common/shared/ContentBlur";
import { HookOverlayContent } from "../../../common/shared/TableComponents";
import type { AdIntelligenceController } from "../../../common/shared/types/types";
import {
    hasAdIntelligenceOverviewAccess,
    isAdIntelligenceNoTouchPreviewVariant,
    openAdIntelligenceHookModal,
} from "../../../common/utils/utils";

import type { TableDataType } from "./columnConfigs";
import { generateSpendImpressionsColumnConfig } from "./columnConfigs";
import { PRICE_RANGE_MAP } from "./preview-performance-widget/constants";

const TableContentWrapper = styled.div`
    .table-container {
        margin-top: 24px;
    }
`;

interface IPreviewPerformanceWidgetCompareProps {
    controller?: AdIntelligenceController;
}

const NoDataBeforeMay2025Component = () => {
    const { translate } = useTranslate();
    return (
        <Stack
            height="100%"
            width="100%"
            justifyContent="center"
            alignItems="center"
        >
            <Text
                fontVariant="subtitle1"
                color={colorsPalettes.carbon[400]}
            >
                {translate("ad.intelligence.overview.spend.and.impressions.data.available.may.2025")}
            </Text>
            <Text
                fontVariant="caption"
                color={colorsPalettes.carbon[400]}
            >
                {translate("ad.intelligence.overview.spend.and.impressions.try.another.period")}
            </Text>
        </Stack>
    );
};

export const PreviewPerformanceWidgetCompareTable = ({
    controller = entityTypeMap.advertiser,
}: IPreviewPerformanceWidgetCompareProps) => {
    const { translate } = useTranslate();
    const subtitleFiltersSecond = useSubtitleFilters("date", "country");
    const { data: performanceData } = usePerformanceQuery(controller);
    const allParams = useDisplayAdvertiserPublisherDataAndParams();
    const { duration, key, ...params } = allParams;
    const isBeforeMay2025 = isRequestedDateBeforeMay2025(duration);
    const hasAdIntelBeta = hasAdIntelligenceOverviewAccess(true);
    const isNoTouchVariant = isAdIntelligenceNoTouchPreviewVariant(true);
    const isHooked = !hasAdIntelBeta;

    const comparingDomains = useMemo(() => key.split(","), [key]);
    const spendImpressionsColumnConfig = generateSpendImpressionsColumnConfig(comparingDomains);
    const obfuscatedData = isBeforeMay2025
        ? []
        : (PRICE_RANGE_MAP.map((item) => {
              return {
                  label: item.label,
                  value: Object.keys(performanceData?.records ?? {}).filter((key) =>
                      item.isValueInRange(performanceData.records[key].spend?.value ?? 0),
                  ),
              };
          }) as TableDataType[]);

    const handleSeeAll = () => {
        allTrackers.dispatchTrackEvent("Internal Link", "click", "spend & impressions/see metrics");
        if (controller === entityTypeMap.advertiser) {
            void displayAdvertisingNavigation.overview.go({
                ...params,
                webSource: devicesTypes.DESKTOP,
                key,
                duration,
            });
        } else {
            void publisherAnalysisNavigation.overview.go({
                ...params,
                webSource: devicesTypes.DESKTOP,
                key,
                duration,
            });
        }
    };

    return (
        <Card
            background={colorsPalettes.carbon[0]}
            overflow="hidden"
        >
            <ContentBlur
                enabled={isHooked && !isBeforeMay2025}
                content={
                    <HookOverlayContent
                        title={translate("ad.intelligence.overview.spend.and.impressions.compare.preview.title")}
                        subtitle={translate("ad.intelligence.overview.spend.and.impressions.compare.preview.subtitle")}
                        buttonText={translate("ad.intelligence.overview.spend.and.impressions.compare.preview.button")}
                        isNoTouchVariant={isNoTouchVariant}
                        onButtonClick={() => openAdIntelligenceHookModal(controller)}
                        dataAutomation="hook-overlay-spend-impressions-compare"
                    />
                }
            >
                <TableContentWrapper>
                    <Table
                        data={obfuscatedData}
                        columnConfig={spendImpressionsColumnConfig}
                        isLoading={false}
                        noData={{
                            Component: isBeforeMay2025 ? NoDataBeforeMay2025Component : undefined,
                        }}
                        theme="white"
                        bordersConfig={{
                            body: false,
                            header: false,
                            lastRowBorder: !isBeforeMay2025,
                            rowBorders: !isBeforeMay2025,
                        }}
                    >
                        <Table.TableTop>
                            <Table.TableTop.Title
                                title={translate(
                                    `ad.intelligence.${controller}.overview.spend.and.impressions.widget.title`,
                                )}
                                tooltip={translate(
                                    `ad.intelligence.${controller}.overview.spend.and.impressions.widget.title.tooltip`,
                                )}
                                filters={subtitleFiltersSecond}
                            />
                        </Table.TableTop>
                    </Table>
                    {!isHooked && !isBeforeMay2025 ? (
                        <Flex
                            alignItems="center"
                            justifyContent="end"
                            width="100%"
                            padding="8px 16px"
                        >
                            <Button
                                type="flat"
                                onClick={handleSeeAll}
                            >
                                {translate("ad.intelligence.overview.spend.and.impressions.preview.button")}
                            </Button>
                        </Flex>
                    ) : null}
                </TableContentWrapper>
            </ContentBlur>
        </Card>
    );
};
